import { useEffect } from "react";
import { useLocation } from "@/lib/router-compat";
import i18n, { isPublicPath, LANG_STORAGE_KEY, SUPPORTED_LANGS, type SupportedLang } from "@/i18n";

/** Langue navigateur ramenée à une langue supportée, sinon `fr`. */
function navigatorLang(): SupportedLang {
  if (typeof navigator === "undefined") return "fr";
  for (const raw of navigator.languages ?? [navigator.language]) {
    const code = (raw || "").split("-")[0];
    if ((SUPPORTED_LANGS as readonly string[]).includes(code)) return code as SupportedLang;
  }
  return "fr";
}

function storedLang(): string | null {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Keeps `<html lang>` in sync with the active i18next language, and re-runs the
 * public/private language rule on every client-side navigation.
 *
 * Sans cela, le `fr` forcé sur les pages publiques (détecteur `publicFr`,
 * évalué une seule fois à l'init) resterait actif après une navigation SPA vers
 * `/auth` ou l'application, y compris pour un navigateur anglais.
 *
 * Un choix explicite stocké dans `localStorage` reste prioritaire.
 *
 * Mount once inside <BrowserRouter>, next to RouteRobotsController.
 */
export default function HtmlLangController() {
  const { pathname } = useLocation();

  useEffect(() => {
    const apply = (lng?: string) => {
      const lang = (lng || i18n.resolvedLanguage || i18n.language || "fr").split("-")[0];
      document.documentElement.lang = lang;
    };
    apply();
    i18n.on("languageChanged", apply);
    return () => {
      i18n.off("languageChanged", apply);
    };
  }, []);

  useEffect(() => {
    if (storedLang()) return; // choix explicite de l'utilisateur : on n'y touche pas
    const target: SupportedLang = isPublicPath(pathname) ? "fr" : navigatorLang();
    const current = (i18n.resolvedLanguage || i18n.language || "fr").split("-")[0];
    if (current !== target) i18n.changeLanguage(target);
  }, [pathname]);

  return null;
}
